import React, { useContext, useMemo } from "react"
import { AD_TYPE_IDS } from "../../../constants/adTypes"
import API from "../../../utils/API"
import "./AiTargeting.css"
import Sweetalert from "../../../utils/Sweetalert"
import ButtonCollapse from "../../ButtonCollapse"
import DiffusionAdCardItem from "../DiffusionAdCardItem"
import LoadingCard from "../../LoadingCard"
import DiffusionAdCardFolder from "../DiffusionAdCardFolder"
import UserContext from "../../../UserContext"
import { openSchedulerModal } from "../Scheduler/SchedulerModal"
import { openAiTargetingModal } from "./AiTargetingModal"

export default function DiffusionAiTargetingAdCard({
  ads,
  adPaths,
  loaded,
  currentAdPath,
  setCurrentAdPath,
  totemSelected,
  aiTargetingAds,
  onAdAdded,
}) {
  const userContext = useContext(UserContext)

  const adsAvailable = useMemo(() => {
    if (!totemSelected) return []
    const configuredAdIds = (aiTargetingAds || []).map((a) => a.adId)
    return ads.filter(
      (ad) => !configuredAdIds.includes(ad.id) && currentAdPath === ad.adPathId
    )
  }, [ads, currentAdPath, totemSelected, aiTargetingAds])

  const adPathsFiltered = useMemo(() => {
    return adPaths.filter((adPath) => currentAdPath === adPath.parent)
  }, [adPaths, currentAdPath])

  const getParentAdPathId = (currentAdPath) => {
    const parentPath = adPaths.find((adPath) => adPath.id === currentAdPath)
    return parentPath ? parentPath.parent : null
  }

  const selectAd = async (ad) => {
    const maxDuration = totemSelected.team_totem.duration

    if (ad.adTypeId === AD_TYPE_IDS.VIDEO && ad.duration > maxDuration) {
      Sweetalert.fireWarningToast(
        "Cette ressource dépasse la limite de " + maxDuration + "s de diffusion par boucle autorisée pour ce totem"
      )

      return
    }

    const existingTargetingAds = (aiTargetingAds || []).filter((a) => !a.isDefault).map((a) => ({ gender: a.gender, ageRange: a.ageRange || [] }))
    const config = await openAiTargetingModal({
      isDefault: false,
      notVideoDuration: ad.adTypeId !== AD_TYPE_IDS.VIDEO ? 10 : 0,
      adTypeId: ad.adTypeId,
      defaultToggleDisabled: (aiTargetingAds || []).some((a) => a.isDefault),
      existingTargetingAds,
      maxDuration: totemSelected.team_totem.duration,
    })

    if (!config) return

    let planification = null
    if (!config.isDefault) {
      planification = await openSchedulerModal()
      if (planification === false) return
    }

    const payload = { adId: ad.id, ...config }
    if (planification != null) payload.planification = planification

    API.POST(
      `/totem-ai-targeting-ad/totem/${totemSelected.id}`,
      payload,
      (response) => {
        Sweetalert.fireSuccessToast(response.data.message)
        onAdAdded({
          id: Date.now().toString(),
          totemId: totemSelected.id,
          adId: ad.id,
          ad,
          ...config,
          ...(planification != null && { planification }),
        })
      }
    )
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Mes ressources</h3>
        <div className="card-tools">
          <ButtonCollapse />
        </div>
      </div>
      <div className="card-body p-0 card-body--block">
        <LoadingCard loaded={loaded}>
          {totemSelected ? (
            <>
              {currentAdPath ? (
                <DiffusionAdCardFolder
                  folderId={getParentAdPathId(currentAdPath)}
                  folderName="Retour"
                  onClick={setCurrentAdPath}
                />
              ) : userContext.canvaLink ? (
                <DiffusionAdCardFolder
                  folderId={"canva"}
                  folderName="Mes ressources Canva"
                  onClick={setCurrentAdPath}
                />
              ) : null}
              {adPathsFiltered.length > 0
                ? adPathsFiltered.map((folder) => (
                  <DiffusionAdCardFolder
                    key={folder.id}
                    folderId={folder.id}
                    folderName={folder.name}
                    onClick={setCurrentAdPath}
                  />
                ))
                : null}
              {adsAvailable.length > 0
                ? adsAvailable.map((ad) => (
                  <DiffusionAdCardItem key={ad.id} ad={ad} onClick={selectAd} />
                ))
                : null}
              {adPathsFiltered.length === 0 && !currentAdPath && adsAvailable.length === 0 ? (
                <div className="alert alert-info m-3">
                  <i className="icon fas fa-info" /> Aucune ressource disponible
                </div>
              ) : null}
            </>
          ) : (
            <div className="alert alert-info m-3">
              <i className="icon fas fa-info" /> Veuillez sélectionner un totem
            </div>
          )}
        </LoadingCard>
      </div>
    </div>
  )
}
